import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Input, 
  Space, 
  Tag, 
  Select, 
  message, 
  Card,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  SearchOutlined, 
  CrownOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { fetchAllUsers } from '../../services/firestoreService';
import { fetchRevenueCatCustomerSubscriptions } from '../../services/revenueCatService';
import { isActivePremium } from '../../utils/userMapper';
import { debugLog, debugPremiumSnapshot } from '../../utils/debugLog';
import { toDate } from '../../utils/timestamp';

const { Option } = Select;

const SubscriptionManagement = () => {
  const { logActivity } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadUsers();
    logActivity('view_subscriptions', 'Viewed subscription management page');
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchText, subscriptionFilter]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const allUsers = await fetchAllUsers();
      const activeUsers = allUsers.filter((u) => u.status !== 'deleted');
      const rcMap = await fetchRevenueCatCustomerSubscriptions(
        activeUsers
          .filter((user) => isActivePremium(user) || user.planId)
          .map((user) => user.uid)
      );
      const merged = activeUsers.map((user) => {
        const rc = rcMap[user.uid];
        if (!rc) return user;
        return {
          ...user,
          subscriptionExpiry: user.subscriptionExpiry || rc.expiresAt || null,
          autoRenewal:
            rc.autoRenewal === undefined || rc.autoRenewal === null
              ? user.autoRenewal
              : rc.autoRenewal,
        };
      });
      debugPremiumSnapshot('Subscriptions', merged);
      merged.sort((a, b) => {
        const aRank = (a.autoRenewal ? 2 : 0) + (isActivePremium(a) ? 1 : 0);
        const bRank = (b.autoRenewal ? 2 : 0) + (isActivePremium(b) ? 1 : 0);
        if (bRank !== aRank) return bRank - aRank;
        const aExp = toDate(a.subscriptionExpiry)?.getTime() || 0;
        const bExp = toDate(b.subscriptionExpiry)?.getTime() || 0;
        return bExp - aExp;
      });
      setUsers(merged);
    } catch (error) {
      debugLog('Subscriptions', 'Failed to load subscriptions', error?.message || error);
      message.error('Failed to load subscriptions from Firebase');
    } finally {
      setLoadingUsers(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Subscription filter
    if (subscriptionFilter === 'premium') {
      filtered = filtered.filter((user) => isActivePremium(user));
    } else if (subscriptionFilter === 'free') {
      filtered = filtered.filter((user) => !isActivePremium(user));
    }

    setFilteredUsers(filtered);
    debugLog('Subscriptions', 'filter result', {
      subscriptionFilter,
      resultCount: filtered.length,
      premiumInResult: filtered.filter((user) => isActivePremium(user)).length,
    });
  };

  const calculateRevenue = () => {
    const premiumUsers = users.filter((u) => isActivePremium(u));
    return (premiumUsers.length * 9.99).toFixed(2);
  };

  const getExpiringCount = () => {
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return users.filter((u) => {
      const expiry = toDate(u.subscriptionExpiry || u.expiryDate || u.expiresAt);
      if (!expiry || Number.isNaN(expiry.getTime())) return false;
      return expiry <= thirtyDaysFromNow && expiry > new Date();
    }).length;
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'username',
      key: 'username',
      render: (username, record) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
            {username?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-medium">{username}</div>
            <div className="text-xs text-gray-500">{record.email || 'No email'}</div>
          </div>
        </div>
      )
    },
    {
      title: 'Subscription',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (_, record) => {
        if (isActivePremium(record)) {
          return <Tag icon={<CrownOutlined />} color="gold" className="font-semibold">PREMIUM</Tag>;
        }
        return <Tag color="default">FREE</Tag>;
      }
    },
    {
      title: 'Expiry Date',
      dataIndex: 'subscriptionExpiry',
      key: 'subscriptionExpiry',
      render: (expiry, record) => {
        const expiryDate = toDate(
          expiry || record.expiryDate || record.expiresAt || record.expirationDate || record.endDate
        );
        if (!expiryDate || Number.isNaN(expiryDate.getTime())) {
          return <span className="text-gray-400">N/A</span>;
        }

        const daysLeft = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));

        let color = 'green';
        if (daysLeft < 0) color = 'red';
        else if (daysLeft < 7) color = 'red';
        else if (daysLeft < 30) color = 'orange';

        return (
          <div>
            <div className="font-medium">{expiryDate.toLocaleDateString()}</div>
            <Tag color={color} className="text-xs mt-1">
              {daysLeft < 0 ? 'Expired' : `${daysLeft} days left`}
            </Tag>
          </div>
        );
      },
      sorter: (a, b) => {
        const aDate = toDate(a.subscriptionExpiry || a.expiryDate);
        const bDate = toDate(b.subscriptionExpiry || b.expiryDate);
        if (!aDate) return 1;
        if (!bDate) return -1;
        return aDate - bDate;
      }
    },
    {
      title: 'Auto Renewal',
      dataIndex: 'autoRenewal',
      key: 'autoRenewal',
      render: (autoRenewal) =>
        autoRenewal ? (
          <Tag color="green">On</Tag>
        ) : (
          <Tag color="default">Off</Tag>
        )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Subscription Management</h1>
        <p className="text-gray-500 mt-1">Manage user subscriptions and billing</p>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Premium Users"
              value={users.filter((u) => isActivePremium(u)).length}
              prefix={<CrownOutlined className="text-yellow-500" />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={calculateRevenue()}
              prefix={<DollarOutlined className="text-green-500" />}
              valueStyle={{ color: '#52c41a' }}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Expiring Soon"
              value={getExpiringCount()}
              prefix={<ClockCircleOutlined className="text-orange-500" />}
              valueStyle={{ color: '#fa8c16' }}
              suffix="/ 30 days"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Free Users"
              value={users.filter((u) => !isActivePremium(u)).length}
              prefix={<RiseOutlined className="text-blue-500" />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card>
        <Space size="middle" wrap>
          <Input
            placeholder="Search by username or email..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            value={subscriptionFilter}
            onChange={setSubscriptionFilter}
            style={{ width: 180 }}
          >
            <Option value="all">All Subscriptions</Option>
            <Option value="free">Free Only</Option>
            <Option value="premium">Premium Only</Option>
          </Select>
        </Space>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="uid"
          loading={loadingUsers}
          pagination={{ 
            pageSize: 10, 
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} users` 
          }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default SubscriptionManagement;
